import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Link from 'flarum/common/components/Link';
import Button from 'flarum/common/components/Button';
import SignUpModal from 'flarum/forum/components/SignUpModal';
import extractText from 'flarum/common/utils/extractText';

import ConfirmEmailNotice from './ConfirmEmailNotice';

import {
  mapServerErrors,
  profileOptions,
  signupNeedsInvite,
  suggestUsername,
  validate,
  FIELD_ORDER,
  type Errors,
  type FieldName,
  type FormValues,
  type ProfileOptions,
} from '../utils/registerForm';
import { canRequest, requestPath } from '../utils/requestForm';
import { legalLinkParams } from '../utils/legalLinks';
import revealablePassword from '../utils/passwordReveal';
import { keepSelectionOnCheckLabel, rememberCheckLabelPress, undoCheckLabelDoubleClick } from '../utils/selectableCheckLabel';

/**
 * M2 · Form A — alta de la Comunidad Umbral, en UNA sola pantalla.
 *
 * Decision de producto del 2026-08-05: se acabo el «crea la cuenta y despues te pedimos seis
 * datos». Identidad, acceso y perfil se envian juntos; el servidor los guarda en el mismo
 * INSERT (`Listener\SaveUmbralProfile`) y abre sesion en la misma respuesta. Por eso este
 * componente no tiene pasos ni modal: cinco secciones escaneables, como el mockup.
 *
 * Estados: formulario (M2) · errores inline al enviar con foco al primero (M2b) · exito con
 * puente a n8n Chile (M2c).
 *
 * El CTA de la tarjeta n8n del exito lleva ya al Form B (T08). Mientras no existia apuntaba a
 * la pagina publica del grupo (D7), y sigue haciendolo cuando la puerta no esta configurada:
 * antes ningun boton que uno que no lleva a ninguna parte.
 */
export default class RegistroPage extends Page {
  /** Valores del formulario. Uno solo para todo: las specs leen por `name`. */
  values!: FormValues;

  /** Errores visibles, por campo. Vacio = M2; con contenido = M2b. */
  errors: Errors = {};

  /** Mensajes del servidor que no supimos anclar a un campo. */
  unmapped: string[] = [];

  loading = false;

  /** Usuario recien creado (M2c). */
  created: any = null;

  /**
   * Catalogo de opciones del servidor. Se lee UNA vez: no cambia en toda la vida de la
   * pagina y `form()` se vuelve a rinder en cada tecla.
   */
  options!: ProfileOptions;

  /**
   * ¿Toco la persona el nombre de usuario? Mientras no lo haga, se sigue derivando de
   * nombre+apellido; en cuanto lo edita, deja de pisarse.
   */
  usernameTouched = false;

  /**
   * Ojo mostrar/ocultar por campo de contraseña (PKG-FORO-PASSWORD-SETTINGS-UX). Vive aqui y
   * no en `revealablePassword` porque el estado es del formulario: se conserva mientras se
   * corrige un error y muere con la pagina.
   */
  revealed: Partial<Record<FieldName, boolean>> = {};

  oninit(vnode: any) {
    super.oninit(vnode);

    app.setTitle(extractText(app.translator.trans('umbral-comunidad.forum.registro.meta_title')));
    app.setTitleCount(0);

    this.options = profileOptions();

    this.values = {
      first_name: '',
      last_name: '',
      email: '',
      alt_email: '',
      username: '',
      company: '',
      role: '',
      source: '',
      topics: [],
      experience: '',
      coding: '',
      password: '',
      password_confirm: '',
      terms: false,
    };
  }

  view() {
    return (
      <div className="CmRegister-container container">
        <div className="CmRegister">{this.body()}</div>
      </div>
    );
  }

  body() {
    // Con sesion abierta el formulario no tiene sentido; y tras el alta esta pantalla es la
    // que muestra M2c, asi que la comprobacion mira antes el estado de exito.
    if (this.created) return this.success();
    if (app.session.user) return this.alreadyIn();
    if (!app.forum.attribute('allowSignUp')) return this.closed();
    // T11 (D): el alta Umbral es publica y este formulario no pide codigo de invitacion. Si el
    // servidor SI lo exigiera (ajuste revertido, o un deploy que aplico el codigo antes que el
    // ajuste), pintar el formulario seria tender una trampa: se rellenan catorce campos y
    // doorman responde 422 por uno que no esta en pantalla. Se dice en su lugar.
    if (signupNeedsInvite()) return this.inviteOnly();

    return this.form();
  }

  form() {
    const t = (key: string, params?: any) => app.translator.trans('umbral-comunidad.forum.registro.' + key, params);
    const options = this.options;
    const errorCount = Object.keys(this.errors).length;

    return (
      /*
        `novalidate` NO es opcional. Los campos de correo son `type="email"` (teclado
        adecuado en movil, autocompletado del navegador), y con la validacion nativa activa
        el navegador BLOQUEA el envio de un correo mal escrito y saca su propio globito:
        `onsubmit` no llega a ejecutarse, no aparece la barra de M2b y el foco no salta al
        primer error. Se descubrio asi — la spec de M2b se quedaba sin errores y sin
        peticion. La validacion la manda el formulario (y detras, el servidor).
      */
      <form className="CmRegister-form" novalidate={true} onsubmit={this.onsubmit.bind(this)}>
        <h1 className="CmRegister-title">{t('title')}</h1>
        <p className="CmRegister-sub">{t('subtitle')}</p>

        {errorCount > 0 && (
          <div className="CmRegister-errorbar" role="alert" data-cm-errorbar={String(errorCount)}>
            {t('error_bar', { count: errorCount })}
          </div>
        )}
        {this.unmapped.map((message) => (
          <div className="CmRegister-errorbar" role="alert">
            {message}
          </div>
        ))}

        {this.section('about', t('section_about'))}
        <div className="CmRegister-row">
          {this.text('first_name', t('label_first_name'), { required: true, autocomplete: 'given-name' })}
          {this.text('last_name', t('label_last_name'), { required: true, autocomplete: 'family-name' })}
        </div>
        {this.text('email', t('label_email'), { required: true, type: 'email', autocomplete: 'email' })}
        {this.text('alt_email', t('label_alt_email'), { hint: t('hint_alt_email'), type: 'email' })}
        {this.text('username', t('label_username'), { required: true, hint: t('hint_username'), autocomplete: 'username' })}

        {this.section('work', t('section_work'))}
        {this.text('company', t('label_company'), { required: true, autocomplete: 'organization' })}
        {this.select('role', t('label_role'), options.role)}
        {this.select('source', t('label_source'), options.source)}

        {this.section('interests', t('section_interests'))}
        {this.chips('topics', t('label_topics'), options.topics, true, t('hint_topics'))}

        {this.section('experience', t('section_experience'))}
        {this.select('experience', t('label_experience'), options.experience)}
        {this.chips('coding', t('label_coding'), options.coding, false)}

        {/* Spec M2 «Acceso» = contrasena + confirmar + casilla de convivencia. Nada mas: el
            codigo de invitacion se fue en T11 (decision D). */}
        {this.section('access', t('section_access'))}
        {this.text('password', t('label_password'), {
          required: true,
          type: 'password',
          autocomplete: 'new-password',
          hint: t('hint_password'),
        })}
        {this.text('password_confirm', t('label_password_confirm'), { required: true, type: 'password', autocomplete: 'new-password' })}

        {/* Los dos documentos que se aceptan van como enlaces reales, en pestaña nueva: ver
            `utils/legalLinks.tsx` (PKG-FORO-SMOKE-FIX). */}
        {this.checkbox('terms', app.translator.trans('umbral-comunidad.forum.registro.label_terms', legalLinkParams()))}

        {/* El boton NUNCA se deshabilita (nota del mockup M2b): siempre explica que falta. */}
        <Button className="Button Button--primary CmRegister-submit" type="submit" loading={this.loading}>
          {t('submit')}
        </Button>

        <p className="CmRegister-legal">{t('legal')}</p>
      </form>
    );
  }

  section(key: string, label: any) {
    return (
      <div className="CmRegister-section" data-cm-section={key}>
        {label}
      </div>
    );
  }

  /**
   * Campo de texto. `data-cm-field` es el ancla: de ahi tira el foco al primer error y de
   * ahi leen las specs, sin depender de la estructura del DOM.
   */
  text(name: FieldName, label: any, opts: { required?: boolean; type?: string; hint?: any; autocomplete?: string } = {}) {
    const error = this.errors[name];

    const attrs: Record<string, any> = {
      id: 'cm-' + name,
      className: 'FormControl CmRegister-input',
      name: name,
      type: opts.type || 'text',
      autocomplete: opts.autocomplete,
      value: this.values[name] as string,
      disabled: this.loading,
      'aria-invalid': error ? 'true' : undefined,
      'aria-describedby': error ? 'cm-err-' + name : undefined,
      oninput: (e: any) => this.setText(name, e.target.value),
    };

    return (
      <div className={'CmRegister-field' + (error ? ' CmRegister-field--error' : '')} data-cm-field={name}>
        <label htmlFor={'cm-' + name}>
          {label}
          {opts.required && <span className="CmRegister-req"> *</span>}
        </label>
        {/* Los campos de contraseña llevan ojo (PKG-FORO-PASSWORD-SETTINGS-UX): el mismo par
            de Configuración. `revealablePassword` decide el `type` según el estado. */}
        {opts.type === 'password' ? (
          revealablePassword(attrs, !!this.revealed[name], () => {
            this.revealed[name] = !this.revealed[name];
          })
        ) : (
          <input {...attrs} />
        )}
        {opts.hint && !error && <p className="CmRegister-hint">{opts.hint}</p>}
        {this.message(name)}
      </div>
    );
  }

  /**
   * Select nativo, no un dropdown propio: en movil abre el selector del sistema, es
   * navegable con teclado sin trabajo extra y no hay que reinventar el estado abierto.
   */
  select(name: FieldName, label: any, values: string[]) {
    const error = this.errors[name];

    return (
      <div className={'CmRegister-field' + (error ? ' CmRegister-field--error' : '')} data-cm-field={name}>
        <label htmlFor={'cm-' + name}>
          {label}
          <span className="CmRegister-req"> *</span>
        </label>
        <select
          id={'cm-' + name}
          className="FormControl CmRegister-select"
          name={name}
          disabled={this.loading}
          aria-invalid={error ? 'true' : undefined}
          value={this.values[name] as string}
          onchange={(e: any) => this.setText(name, e.target.value)}
        >
          <option value="">{app.translator.trans('umbral-comunidad.forum.registro.select_empty')}</option>
          {values.map((value) => (
            <option value={value}>{this.optionLabel(name, value)}</option>
          ))}
        </select>
        {this.message(name)}
      </div>
    );
  }

  /**
   * Chips. `multi` decide si son casillas (temas de interes) o excluyentes (experiencia en
   * programacion). Se pintan como `<button type="button">` con `aria-pressed`: un `<span>`
   * clicable no llega con teclado y el mockup los quiere del tamano de un boton (≥44px).
   */
  chips(name: FieldName, label: any, values: string[], multi: boolean, hint?: any) {
    const error = this.errors[name];
    const selected = multi ? (this.values[name] as string[]) : [this.values[name] as string];

    return (
      <div className={'CmRegister-field' + (error ? ' CmRegister-field--error' : '')} data-cm-field={name}>
        <span className="CmRegister-label">
          {label}
          <span className="CmRegister-req"> *</span>
        </span>
        {hint && <p className="CmRegister-hint">{hint}</p>}
        <div className="CmRegister-opts" role={multi ? 'group' : 'radiogroup'}>
          {values.map((value) => {
            const on = selected.indexOf(value) >= 0;

            return (
              <button
                type="button"
                className={'CmRegister-opt' + (on ? ' is-on' : '')}
                data-cm-opt={value}
                aria-pressed={on ? 'true' : 'false'}
                disabled={this.loading}
                onclick={() => this.toggle(name, value, multi)}
              >
                {this.optionLabel(name, value)}
              </button>
            );
          })}
        </div>
        {this.message(name)}
      </div>
    );
  }

  checkbox(name: FieldName, label: any) {
    const error = this.errors[name];

    return (
      <div className={'CmRegister-check' + (error ? ' CmRegister-field--error' : '')} data-cm-field={name}>
        {/* El guarda deja copiar el texto de convivencia sin marcar la casilla (T10). */}
        <label onmousedown={rememberCheckLabelPress} onclick={keepSelectionOnCheckLabel} ondblclick={undoCheckLabelDoubleClick}>
          <input
            id={'cm-' + name}
            type="checkbox"
            name={name}
            checked={!!this.values[name]}
            disabled={this.loading}
            aria-invalid={error ? 'true' : undefined}
            onchange={(e: any) => {
              (this.values as any)[name] = !!e.target.checked;
              this.clear(name);
            }}
          />
          <span>{label}</span>
        </label>
        {this.message(name)}
      </div>
    );
  }

  message(name: FieldName) {
    const error = this.errors[name];

    if (!error) return null;

    return (
      <p className="CmRegister-msg" id={'cm-err-' + name} data-cm-error={name}>
        {error}
      </p>
    );
  }

  optionLabel(group: string, value: string) {
    return app.translator.trans(`umbral-comunidad.forum.registro.options.${group}.${value}`);
  }

  setText(name: FieldName, value: string) {
    (this.values as any)[name] = value;

    if (name === 'username') this.usernameTouched = true;

    // Sugerencia viva del usuario mientras nadie lo haya tocado (ver `suggestUsername`).
    if ((name === 'first_name' || name === 'last_name') && !this.usernameTouched) {
      this.values.username = suggestUsername(this.values.first_name, this.values.last_name);
      this.clear('username');
    }

    this.clear(name);
  }

  toggle(name: FieldName, value: string, multi: boolean) {
    if (multi) {
      const list = this.values[name] as string[];
      const at = list.indexOf(value);

      if (at >= 0) list.splice(at, 1);
      else list.push(value);
    } else {
      // Excluyente pero deseleccionable: volver a pulsar la opcion activa la quita, que es
      // lo que espera quien se equivoco de chip y no tiene un «ninguna» al que volver.
      (this.values as any)[name] = this.values[name] === value ? '' : value;
    }

    this.clear(name);
  }

  /** El error de un campo desaparece en cuanto se toca: no se regana mientras se corrige. */
  clear(name: FieldName) {
    if (this.errors[name] !== undefined) {
      delete this.errors[name];
    }
  }

  onsubmit(e: SubmitEvent) {
    e.preventDefault();

    if (this.loading) return;

    this.unmapped = [];
    this.errors = validate(this.values);

    if (Object.keys(this.errors).length) {
      this.focusFirstError();
      return;
    }

    this.loading = true;

    // `POST {baseUrl}/register` y no `/api/users`: es la ruta del foro
    // (`Forum\Controller\RegisterController`), que envuelve el body en `data.attributes`,
    // lo reenvia a la API y ADEMAS abre la sesion en la misma respuesta. Un solo viaje para
    // crear cuenta, guardar perfil e iniciar sesion.
    app
      .request<any>({
        url: app.forum.attribute('baseUrl') + '/register',
        method: 'POST',
        body: this.payload(),
        errorHandler: this.onerror.bind(this),
      })
      .then(
        (payload: any) => {
          this.loading = false;

          const user = payload && payload.data ? app.store.pushPayload(payload) : null;

          if (user) {
            // La sesion YA existe en el servidor (cookie incluida). Reflejarla aqui evita el
            // `window.location.reload()` del modal nativo, que se llevaria por delante la
            // pantalla de exito M2c. El token CSRF se refresca solo: `StartSession` devuelve
            // el nuevo en la cabecera `X-CSRF-Token` de cada respuesta y `app.request` lo
            // guarda.
            app.session.user = user as any;

            // El alta termina con un aviso en la bandeja global, no con el mensaje generico
            // de error del core. El copy cambia segun el estado REAL devuelto por la API:
            // una invitacion puede entregar la cuenta ya confirmada, mientras que el alta
            // publica necesita que la persona abra el enlace que acaba de recibir.
            this.showRegistrationToast(user);
          }

          this.created = user;
          m.redraw();
        },
        () => {
          this.loading = false;
          m.redraw();
        }
      );
  }

  /** La misma lectura gobierna el toast y el cuerpo de M2c; no pueden contradecirse. */
  isCreatedUserConfirmed(user: any): boolean {
    return !user || typeof user.isEmailConfirmed !== 'function' || user.isEmailConfirmed();
  }

  /** Aviso persistente de la bandeja de Flarum tras completar el alta. */
  showRegistrationToast(user: any): void {
    const state = this.isCreatedUserConfirmed(user) ? 'confirmed' : 'unconfirmed';
    const t = (part: 'title' | 'body') => app.translator.trans(`umbral-comunidad.forum.registro.toast_${state}_${part}`);

    app.alerts.show({ type: 'success', title: t('title') }, t('body'));
  }

  payload() {
    const body: any = {
      username: this.values.username.trim(),
      email: this.values.email.trim(),
      password: this.values.password,
      // Objeto propio: no se mezcla con los atributos del core ni con los de doorman, y el
      // listener del servidor lo usa como marcador de «esta alta viene del Form A».
      umbralProfile: {
        first_name: this.values.first_name.trim(),
        last_name: this.values.last_name.trim(),
        alt_email: this.values.alt_email.trim() || null,
        company: this.values.company.trim(),
        role: this.values.role,
        source: this.values.source,
        topics: this.values.topics,
        experience: this.values.experience,
        coding: this.values.coding,
        terms: this.values.terms,
      },
    };

    // El portador de la invitación, si se llegó por un enlace (PKG-FORO-INVITE-AUTOGRANT).
    //
    // HERMANO de `umbralProfile`, no dentro: el listener del perfil valida su objeto con reglas
    // y sólo devuelve las claves que tienen una, así que un portador ahí quedaría a un paso de
    // aparecer en el cuerpo de un 422. El servidor lo digiere y lo olvida en la misma petición.
    //
    // La clave se OMITE cuando no hay invitación: así el cuerpo de un alta normal no cambia.
    const invite = this.pendingInvite();

    if (invite) {
      body.umbralInvite = invite;
    }

    return body;
  }

  onerror(error: any) {
    // PRIMERO soltar el candado. `requestErrorCatch` del core llama a este manejador ANTES
    // de rechazar la promesa, asi que sin esta linea el `m.redraw.sync()` de
    // `focusFirstError()` rinde los campos con `disabled={this.loading}` en true — y
    // `.focus()` sobre un input deshabilitado no hace nada. El resultado era que los errores
    // del servidor mostraban el mensaje pero no movian el foco, al reves que los de cliente.
    this.loading = false;

    const mapped = mapServerErrors(error && error.response);

    this.errors = mapped.errors;
    this.unmapped = mapped.unmapped;

    // Un error sin detalle util (500, red caida) no puede quedar en silencio: se deja que el
    // manejador por defecto de Flarum saque su alerta.
    if (!Object.keys(this.errors).length && !this.unmapped.length) {
      if (error && error.alert) app.alerts.show(error.alert, error.alert.content);
      return;
    }

    this.focusFirstError();
  }

  /**
   * Foco al primer error EN EL ORDEN DEL FORMULARIO (M2b), no en el orden en que llegaron:
   * el servidor no promete ninguno y saltar al ultimo campo desorienta.
   */
  focusFirstError() {
    m.redraw.sync();

    const first = FIELD_ORDER.find((name) => this.errors[name] !== undefined);

    if (!first) return;

    const wrapper = document.querySelector(`[data-cm-field="${first}"]`);
    const focusable = wrapper && (wrapper.querySelector('input, select, button') as HTMLElement | null);

    if (focusable) focusable.focus();
    if (wrapper) wrapper.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  /** M2c — exito, con el puente a n8n Chile. */
  success() {
    const t = (key: string, params?: any) => app.translator.trans('umbral-comunidad.forum.registro.' + key, params);
    const name = this.values.first_name.trim();
    // El aviso «revisa tu correo» solo aparece si de verdad hace falta: con una invitacion
    // que activa (`doorkey.activates`) la cuenta ya viene confirmada y decir lo contrario
    // mandaria a esperar un correo que no llega.
    const confirmed = this.isCreatedUserConfirmed(this.created);

    return (
      <div className="CmRegister-state" data-cm-state="success">
        <span className="CmRegister-ic">✓</span>
        <h1 className="CmRegister-title">{t('success_title', { name })}</h1>
        <p className="CmRegister-sub">{confirmed ? t('success_body') : t('success_body_unconfirmed')}</p>

        {/*
          T12 · objetivo 2 — el paso que faltaba en esta pantalla.

          Hasta ahora el alta sin confirmar solo dejaba una FRASE («revisa tu correo») y ninguna
          salida: si el correo no llegaba —o se borraba sin querer— la persona se quedaba con una
          cuenta que no puede escribir y sin nada que pulsar. El aviso trae el reenvio, dice a que
          direccion se mando y avisa de spam.

          Va DESPUES del CTA a las discusiones a proposito: leer el foro si se puede sin confirmar,
          asi que el camino principal sigue abierto y esto es la aclaracion de lo que falta.
        */}
        {confirmed ? null : <ConfirmEmailNotice email={this.values.email.trim()} />}

        {/*
          T12B · objetivo 3 — quien llegó aquí desde una invitación vuelve a ella.

          `InvitacionPage` deja el token en `sessionStorage` justo antes de mandar a este
          formulario. Si el navegador bloquea ese storage, lo transporta en el fragmento,
          que nunca llega al servidor. Sin este puente, el invitado se daba de alta, caía en
          `/discusiones` y perdía la invitación: tenía que volver a buscar el correo.

          Va DELANTE del CTA normal porque para esa persona es la acción que la trajo.
        */}
        {this.pendingInvite() ? (
          <Link
            className="Button Button--primary CmRegister-submit"
            data-cm-back-to-invite="1"
            external={true}
            href={app.route('umbral-comunidad.invitacion') + '#' + encodeURIComponent(this.pendingInvite()!)}
            onclick={(event: Event) => {
              event.preventDefault();
              window.location.replace(app.route('umbral-comunidad.invitacion') + '#' + encodeURIComponent(this.pendingInvite()!));
            }}
          >
            {t('back_to_invite')}
          </Link>
        ) : (
          <Link className="Button Button--primary CmRegister-submit" href={app.route('umbral-comunidad.discussions')}>
            {t('success_cta')}
          </Link>
        )}

        {/*
          Puente a n8n Chile (Spec §10: el CTA vive tambien en el exito del Form A).

          T07 mandaba a D7 porque el Form B no existia. Ahora existe, y `canRequest()` decide:
          si la puerta esta configurada, el CTA lleva al formulario de verdad; si no, se
          conserva el enlace a D7 en vez de ofrecer un boton que respondería «no disponible».
          Es la misma regla de honestidad, aplicada a los dos casos.
        */}
        <div className="CmRegister-n8card">
          <span className="cm-chip cm-closed">{t('n8_chip')}</span>
          <p>{t('n8_body')}</p>
          {canRequest() ? (
            <Link className="cm-btn-n8n-out" href={requestPath()} data-cm-request-cta="open">
              {t('n8_cta')}
            </Link>
          ) : (
            <Link className="cm-btn-n8n-out" href={app.route('umbral-comunidad.n8nChile')}>
              {t('n8_cta_fallback')}
            </Link>
          )}
        </div>
      </div>
    );
  }

  /**
   * Token de la invitacion que trajo a esta persona, si lo hay.
   *
   * Misma clave que `InvitacionPage.STASH_KEY`. El fragmento es el fallback para navegación
   * privada o almacenamiento lleno y tiene prioridad para no reutilizar un stash anterior.
   */
  pendingInvite(): string | null {
    const raw = window.location.hash.replace(/^#/, '');

    if (raw) {
      try {
        const decoded = decodeURIComponent(raw);

        if (/^[A-Za-z0-9_-]{32}$/.test(decoded)) return decoded;
      } catch (e) {
        if (/^[A-Za-z0-9_-]{32}$/.test(raw)) return raw;
      }
    }

    try {
      const stashed = sessionStorage.getItem('umbral.invite.token');

      return stashed && /^[A-Za-z0-9_-]{32}$/.test(stashed) ? stashed : null;
    } catch (e) {
      return null;
    }
  }

  /** Ya hay sesion: el alta no aplica. */
  alreadyIn() {
    const t = (key: string) => app.translator.trans('umbral-comunidad.forum.registro.' + key);

    return (
      <div className="CmRegister-state" data-cm-state="already">
        <h1 className="CmRegister-title">{t('already_title')}</h1>
        <p className="CmRegister-sub">{t('already_body')}</p>
        <Link className="Button Button--primary CmRegister-submit" href={app.route('umbral-comunidad.discussions')}>
          {t('success_cta')}
        </Link>
      </div>
    );
  }

  /**
   * El servidor sigue exigiendo codigo de invitacion (T11 · D).
   *
   * No es el mismo caso que `closed()`: alli el foro tiene el alta apagada del todo
   * (`allowSignUp` en false) y no hay nada que hacer; aqui el alta funciona, pero solo con una
   * llave — y quien la tenga la usara en el modal nativo de Flarum, que es donde `fof/doorman`
   * pinta su campo. Se ofrece esa salida en vez de fingir que el formulario largo sirve.
   */
  inviteOnly() {
    const t = (key: string) => app.translator.trans('umbral-comunidad.forum.registro.' + key);

    return (
      <div className="CmRegister-state" data-cm-state="invite-only">
        <span className="CmRegister-ic">🔒</span>
        <h1 className="CmRegister-title">{t('invite_only_title')}</h1>
        <p className="CmRegister-sub">{t('invite_only_body')}</p>
        <Button className="Button Button--primary CmRegister-submit" onclick={() => app.modal.show(SignUpModal)}>
          {t('invite_only_cta')}
        </Button>
      </div>
    );
  }

  /** Altas cerradas en el panel: se dice, no se muestra un formulario que fallaria. */
  closed() {
    const t = (key: string) => app.translator.trans('umbral-comunidad.forum.registro.' + key);

    return (
      <div className="CmRegister-state" data-cm-state="closed">
        <h1 className="CmRegister-title">{t('closed_title')}</h1>
        <p className="CmRegister-sub">{t('closed_body')}</p>
        {/*
          `n8_cta_fallback` y NO `n8_cta`: este enlace va a D7, y desde T08 `n8_cta` dice
          «Solicitar acceso a n8n Chile». Un boton con ese texto que aterriza en una pagina
          informativa promete algo que no hace — y aqui, ademas, a alguien sin sesion, que es
          justo quien no puede solicitar nada.
        */}
        <Link className="Button Button--primary CmRegister-submit" href={app.route('umbral-comunidad.n8nChile')}>
          {t('n8_cta_fallback')}
        </Link>
      </div>
    );
  }
}
